import React, { useCallback, useEffect, useState } from 'react';
import Icon from '~/app/common/Icon';
import classes from './style.module.scss';
import Button from '~/app/common/Button';
import Text from '~/app/modules/form/Text';
import Select from '~/app/modules/form/Select';
import useTranslation from '~/app/hooks/useTranslation';
import { ExternalResource } from '~/training/types';

interface ExternalComponentProps {
  initialItems?: ExternalResource[];
  onExternalResourceChange: (items: ExternalResource[]) => void;
}

export type ExternalComponent = React.FC<ExternalComponentProps>;

const External: ExternalComponent = ({ initialItems = [], onExternalResourceChange }) => {

  const { t } = useTranslation("training.external");

  const linkTypeOptions = [
    { text: 'Video', value: 1 },
    { text: 'PDF', value: 2 },
    { text: 'Nettside', value: 3 },
    { text: 'Annet', value: 4 }
  ]

  const [isExpanded, setIsExpanded] = useState(false);
  const [externalResources, setExternalResources] = useState<ExternalResource[]>(initialItems);

  const toggleExpand = () => {
    if (isExpanded) {
      const hasIncomplete = externalResources.some(
        resource => !resource.title.trim() || !resource.url.trim()
      );
      if (hasIncomplete) {
        return;
      }
      setIsExpanded(false);
    } else {
      setIsExpanded(true);
    }
  };

  const validateUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const updateExternalResourceItems = useCallback((newItems: ExternalResource[]) => {
    setExternalResources(newItems);
    onExternalResourceChange(newItems);
  }, [onExternalResourceChange]);

  const addExternalResource = () => {
    if (externalResources.length >= 3) return;
    
    const newResource: ExternalResource = {
      id: 'Manual_' + Date.now().toString(),
      title: '',
      type: 1,
      url: ''
    };
    const newItems = [...externalResources, newResource]
    updateExternalResourceItems(newItems);
  };

  const removeExternalResource = (id: string) => {
    const newItems = externalResources.filter(resource => resource.id !== id);
    updateExternalResourceItems(newItems);
  };

  const updateExternalResource = (id: string, field: keyof ExternalResource, value: string | number) => {
    const resourceIndex = externalResources.findIndex(resource => resource.id === id);
    if (resourceIndex === -1) return;

    const resource = externalResources[resourceIndex];
    if (resource[field] === value) return;

    const newItems = [...externalResources];
    newItems[resourceIndex] = { ...resource, [field]: value };
  
    updateExternalResourceItems(newItems);
  };

  useEffect(() => {
    if (initialItems?.length) {
      setExternalResources(initialItems);
    }
  }, [initialItems]);

  return (
    <>
      <div onClick={toggleExpand} className={classes['external-switch']}>
        {t('externalContent')}
        {isExpanded ? <Icon name='keyboard_arrow_up' /> : <Icon name='keyboard_arrow_down' />}
      </div>

      {isExpanded && (
        <>
          {externalResources.length < 3 && (
            <Button className={classes['add-link-button']} onClick={addExternalResource}><Icon name='add' /> {t('addLink')}</Button>
          )}
          {externalResources.map((resource) => (
            <div key={resource.id} className={classes['resource-item']}>
              <div className={classes['resource-item-heading']}>
                <div className={classes['link-heading']}>{t('link')}</div>
                <button type="button" onClick={() => removeExternalResource(resource.id)}>
                  <Icon name='delete_outline'/>{t('remove')}
                </button>
              </div>

              <div>
                <div className={classes['mt-12']}>
                  <div className={classes['link-heading']}>{t('title')} <span className={classes['required']}>*</span></div>
                  <Text required
                    value={resource.title}
                    onChange={(e) => updateExternalResource(resource.id, 'title', e.target.value)}
                    placeholder={`${t('enterTitle') + '...'}`}
                    className={classes['mt-4']}
                  />
                </div>

                <div className={classes['mt-12']}>
                  <div className={classes['link-heading']}>{t('type')} <span className={classes['required']}>*</span></div>
                  <Select required
                    defaultValue={resource.type}
                    onChange={e => updateExternalResource(resource.id, 'type', e.target.value)}
                    options={linkTypeOptions}
                  />
                </div>

                <div className={classes['mt-12']}>
                  <div className={classes['link-heading']}>URL <span className={classes['required']}>*</span></div>
                  <Text required
                    id={`url-${resource.id}`}
                    type="url"
                    value={resource.url}
                    onChange={(e) => updateExternalResource(resource.id, 'url', e.target.value)}
                    placeholder="https://..."
                    className={classes['mt-4']}
                  />
                  {resource.url && !validateUrl(resource.url) && (
                    <p className={classes['error']}>{t('invalidUrl')}</p>
                  )}
                  {resource.url && !resource.title && (
                    <p className={classes['error']}>{t('urlRequired')}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
          <p className={classes['external-info']}>{t('info')}</p>
        </>
      )}
    </>
  );
};

export default External;
